import { z } from 'zod';
/**
 * Índice de utilidad del EQ-5D-5L · crosswalk versionado.
 *
 * El índice **no se mide: se deriva**. El dato primario es el perfil de cinco
 * dígitos; el índice sale de aplicarle un *value set* de una población concreta.
 * Para Argentina no existe value set 5L, así que se usa el **crosswalk de van
 * Hout (2012)** contra el value set **3L argentino de Augustovski (2009)**, que
 * es el criterio que Fundación tiene que confirmar.
 *
 * **Hoy no hay ningún value set cargado, y eso es deliberado.** El value set son
 * 243 utilidades tabuladas de una publicación: no se reconstruyen de memoria ni
 * se aproximan con una fórmula parecida, por la misma razón por la que ADR-008
 * prohíbe un LOINC sin verificar. Mientras el registro esté vacío,
 * `calcularIndice` devuelve `null` y **no se escribe la Observation
 * `eq5d5l-indice`**: el perfil y el VAS —que son el dato real— se registran
 * igual. El día que llegue la tabla, entra acá con su versión y el índice
 * aparece sin tocar nada más.
 *
 * El índice **no lleva color** (mapa de datos validado por Fundación, 2026-08):
 * alcanzan el perfil y el termómetro.
 */
export const valueSetEq5dSchema = z.strictObject({
    /** Identificador estable que viaja en la metadata de la Observation. */
    id: z.string().min(1),
    /** Población del value set. */
    pais: z.string().min(1),
    /** Publicación de la que sale la tabla. */
    fuente: z.string().min(1),
    /** Versión de la tabla, para poder recalcular índices viejos igual que antes. */
    version: z.string().min(1),
    /** Cómo se pasa del perfil 5L al value set: directo o por crosswalk. */
    metodo: z.enum(['directo-5l', 'crosswalk-van-hout-3l']),
    /**
     * Utilidad por perfil de cinco dígitos. Completa: 3125 perfiles en un value
     * set 5L directo, 243 en un crosswalk contra 3L, que mapea cada perfil 5L a
     * uno 3L antes de buscar.
     */
    utilidades: z.record(z.string().regex(/^[1-5]{5}$/), z.number()),
});
/**
 * Los value sets disponibles.
 *
 * Vacío hasta que Fundación confirme el criterio y la tabla esté cargada de
 * primera mano. Ver `dev/escalas-datos-fhir.md` §2.4 y `pendientes-fundacion.md`.
 */
export const VALUE_SETS_EQ5D = Object.freeze([]);
/** El que se usa por defecto, cuando haya uno. */
export const VALUE_SET_EQ5D_POR_DEFECTO = null;
/**
 * Busca el perfil en un value set concreto.
 *
 * Está separado de `calcularIndice` para poder **probar la búsqueda antes de
 * tener la tabla real**: si el lookup viviera pegado al registro vacío, el día
 * que llegue el value set de Fundación nada probaría que funciona, y se
 * estrenaría en producción con datos de pacientes.
 */
export function aplicarValueSet(perfil, valueSet) {
    const valor = valueSet.utilidades[perfil];
    if (valor === undefined)
        return null;
    return {
        valor,
        valueSetId: valueSet.id,
        valueSetVersion: valueSet.version,
        metodo: valueSet.metodo,
        fuente: valueSet.fuente,
    };
}
/**
 * Índice de utilidad del perfil, o `null` si no hay value set con qué calcularlo.
 *
 * `null` no es un error: es el estado actual del proyecto y la Observation
 * simplemente no se emite. Devolver 0 o un promedio sería peor que no tener el
 * dato, porque un cero se grafica.
 */
export function calcularIndice(perfil, valueSetId, disponibles = VALUE_SETS_EQ5D) {
    const id = valueSetId ?? VALUE_SET_EQ5D_POR_DEFECTO;
    if (!id)
        return null;
    const valueSet = disponibles.find((candidato) => candidato.id === id);
    if (!valueSet)
        return null;
    return aplicarValueSet(perfil, valueSet);
}
//# sourceMappingURL=crosswalk-eq5d.js.map